import React, { useEffect, useState } from "react";
import {
  Stack,
  Typography,
  Paper,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Avatar,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Iconify, MuiDrawer, Label } from "src/components";
import LabelRole from "src/components/LabelRole";
import { capitalCase } from "change-case";
import { _section_stats } from "src/DAL/project";
import { s3baseUrl } from "src/config";
import { useSnackbar } from "notistack";
import { truncateText } from "src/utils";

// ===============================================================
export default function SectionStatsDrawer({
  open,
  onClose,
  sectionId,
  sectionTitle,
}) {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Functions =========================
  const getSectionStats = async () => {
    if (!sectionId) {
      return;
    }
    setLoading(true);
    const result = await _section_stats(sectionId);
    if (result.code == 200) {
      setStatsData(result.data);
      setLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setLoading(false);
    }
  };

  // useEffects =========================
  useEffect(() => {
    if (open && sectionId) {
      getSectionStats();
    }
  }, [open, sectionId]);

  return (
    <>
      <MuiDrawer
        paperProps={{ width: { xs: 1, sm: 540, md: 490 } }}
        isOpen={open}
        onClose={onClose}
        title={
          <Tooltip title={sectionTitle || "Section Stats"}>
            <Typography sx={{ fontWeight: "700" }}>
              {truncateText(sectionTitle, 50)}
            </Typography>
          </Tooltip>
        }
        bottomComponent={null}
      >
        {loading ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{ height: "100%", minHeight: "85vh" }}
          >
            <CircularProgress size={40} />
          </Stack>
        ) : statsData ? (
          <Stack spacing={3} sx={{ pb: 2 }}>
            {/* Task Overview Section */}
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
                sx={{ mb: 1 }}
              >
                <Tooltip title="Total Number of Tasks in This Section">
                  <Box
                    sx={{
                      display: "inline-flex",
                      color: "text.secondary",
                      "&:hover": {
                        color: "info.main",
                      },
                    }}
                  >
                    <Iconify
                      icon="mdi:clipboard-list-outline"
                      fontSize="18px"
                    />
                  </Box>
                </Tooltip>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Section Tasks Overview
                </Typography>
              </Stack>
              <Grid container spacing={1.5}>
                {[
                  {
                    label: "Total Tasks",
                    color: "#ecb5d9",
                    value: statsData.task_counts?.total_tasks,
                  },
                  {
                    label: "Completed",
                    color: "#00AB55",
                    value: statsData.task_counts?.completed_tasks,
                  },
                  {
                    label: "Pending",
                    color: "#FFC107",
                    value: statsData.task_counts?.pending_tasks,
                  },
                  {
                    label: "In Progress",
                    color: "#3366FF",
                    value: statsData.task_counts?.in_progress_tasks,
                  },
                  {
                    label: "Verified",
                    color: "#969292",
                    value: statsData.task_counts?.verified_tasks,
                  },
                  {
                    label: "Cancelled",
                    color: "#FF4842",
                    value: statsData.task_counts?.cancelled_tasks,
                  },
                  {
                    label: "Ready for Testing",
                    color: "#a4bdfc",
                    value: statsData.task_counts?.ready_for_testing_tasks,
                  },
                  {
                    label: "Custom Status",
                    color: "#9C27B0",
                    value: statsData.task_counts?.no_status_tasks,
                  },
                ].map(({ label, color, value }) => (
                  <Grid item xs={6} key={label}>
                    <Paper
                      sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        bgcolor: "#F7F9F8",
                        boxShadow: "0 1px 3px rgba(145, 158, 171, 0.2)",
                        border: "1px solid #cecece3d",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.8}
                        sx={{ mb: 0.5 }}
                      >
                        <Iconify
                          icon="ph:circle-fill"
                          width={10}
                          sx={{ color }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                      </Stack>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {value || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Total Time Overview Section */}
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
                sx={{ mb: 1 }}
              >
                <Tooltip title="Total Duration of All Tasks in This Section">
                  <Box
                    sx={{
                      display: "inline-flex",
                      color: "text.secondary",
                      "&:hover": {
                        color: "info.main",
                      },
                    }}
                  >
                    <Iconify icon="mdi:clock-outline" fontSize="18px" />
                  </Box>
                </Tooltip>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  All Tasks Time
                </Typography>
              </Stack>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  bgcolor: "#F7F9F8",
                  boxShadow: "0 1px 3px rgba(145, 158, 171, 0.2)",
                  border: "1px solid #cecece3d",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Planned Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {statsData.time_stats?.planned_time?.formatted || "0s"}
                  </Typography>
                </Stack>
                <Divider
                  sx={{ mt: "6px", mb: "6px", borderColor: "#b8b8b8ff" }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Spending Time
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    {statsData.time_stats?.spending_time?.formatted || "0s"}
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            {/* Team Stats Section */}
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
                sx={{ mb: 1 }}
              >
                <Tooltip title="Team Stats include the total tasks created by the selected member & tasks assigned to the selected member and the total time spent across all tasks for the selected member.">
                  <Box
                    sx={{
                      display: "inline-flex",
                      color: "text.secondary",
                      "&:hover": {
                        color: "info.main",
                      },
                    }}
                  >
                    <Iconify icon="mdi:account-group-outline" fontSize="18px" />
                  </Box>
                </Tooltip>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Team Stats
                </Typography>
              </Stack>
              <Stack spacing={2}>
                {statsData.team && statsData.team.length > 0 ? (
                  [...statsData.team]
                    .sort((a, b) => {
                      const rolePriority = { admin: 0, manager: 1 };
                      const pa = rolePriority[a.role?.toLowerCase()] ?? 2;
                      const pb = rolePriority[b.role?.toLowerCase()] ?? 2;
                      return pa - pb;
                    })
                    .map((member) => (
                      <Accordion
                        key={member.user_id}
                        elevation={3}
                        disableGutters
                        defaultExpanded
                        sx={{
                          border: "1px solid #cecece3d",
                          borderRadius: "8px !important",
                          "&:before": { display: "none" },
                          overflow: "hidden",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            minHeight: "unset",
                            "& .MuiAccordionSummary-content": {
                              my: 0.5,
                              alignItems: "center",
                              justifyContent: "space-between",
                            },
                          }}
                        >
                          {/* Left: Avatar + Name + Email */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Avatar
                              src={member.image ? s3baseUrl + member.image : ""}
                              alt={`${member.first_name} ${member.last_name}`}
                              sx={{ width: 40, height: 40, flexShrink: 0 }}
                            >
                              {member.first_name?.substring(0, 2)}
                            </Avatar>
                            <Stack spacing={0.3} sx={{ minWidth: 0 }}>
                              <Typography variant="body1" noWrap>
                                {member.first_name} {member.last_name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {member.email}
                              </Typography>
                            </Stack>
                          </Stack>

                          {/* Right: Role badge */}
                          <Box sx={{ ml: 1, mr: 1, flexShrink: 0 }}>
                            <LabelRole role={member.role || "team"}>
                              {capitalCase(member.role ?? "Team")}
                            </LabelRole>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                          <Stack spacing={1}>
                            {/* Tasks */}
                            <Paper
                              sx={{
                                background: "#F7F9F8",
                                boxShadow: "none",
                                borderRadius: "8px",
                                p: 1.5,
                              }}
                            >
                              <Typography
                                fontWeight={600}
                                fontSize="12px"
                                sx={{ mb: 1 }}
                              >
                                Tasks
                              </Typography>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ flexWrap: "wrap" }}
                              >
                                {[
                                  {
                                    title: "Pending",
                                    color: "#FFC107",
                                    value: member.task_counts?.pending_tasks,
                                  },
                                  {
                                    title: "In Progress",
                                    color: "#3366FF",
                                    value:
                                      member.task_counts?.in_progress_tasks,
                                  },
                                  {
                                    title: "Ready for Testing",
                                    color: "#a4bdfc",
                                    value:
                                      member.task_counts
                                        ?.ready_for_testing_tasks,
                                  },
                                  {
                                    title: "Completed",
                                    color: "#00AB55",
                                    value: member.task_counts?.completed_tasks,
                                  },
                                  {
                                    title: "Verified",
                                    color: "#969292",
                                    value: member.task_counts?.verified_tasks,
                                  },
                                  {
                                    title: "Cancelled",
                                    color: "#FF4842",
                                    value: member.task_counts?.cancelled_tasks,
                                  },
                                  {
                                    title: "Custom Status",
                                    color: "#9C27B0",
                                    value: member.task_counts?.no_status_tasks,
                                  },
                                ].map(({ title, color, value }) => (
                                  <Tooltip title={title} key={title}>
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={0.5}
                                    >
                                      <Box
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: "50%",
                                          bgcolor: color,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {value || 0}
                                      </Typography>
                                    </Stack>
                                  </Tooltip>
                                ))}
                              </Stack>
                            </Paper>

                            {/* Time Stats */}
                            <Paper
                              sx={{
                                background: "#F7F9F8",
                                boxShadow: "none",
                                borderRadius: "8px",
                                p: 1.5,
                              }}
                            >
                              <Typography
                                fontWeight={600}
                                fontSize="12px"
                                sx={{ mb: 1 }}
                              >
                                Time Stats of All Tasks
                              </Typography>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  color="text.secondary"
                                  sx={{
                                    fontSize: "11px",
                                  }}
                                >
                                  Planned Time
                                </Typography>
                                {member.time_stats?.planned_time?.formatted &&
                                member.time_stats?.planned_time?.formatted !==
                                  "0s" ? (
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {member.time_stats?.planned_time?.formatted}
                                  </Typography>
                                ) : (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                  >
                                    <Iconify
                                      icon="eva:info-outline"
                                      sx={{
                                        color: "text.secondary",
                                        width: 14,
                                        height: 14,
                                      }}
                                    />
                                    <Typography
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "11px",
                                      }}
                                    >
                                      No Planned Time
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                              <Divider
                                sx={{
                                  mt: "6px",
                                  mb: "6px",
                                  borderColor: "#b8b8b8ff",
                                }}
                              />
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  color="text.secondary"
                                  sx={{
                                    fontSize: "11px",
                                  }}
                                >
                                  Spending Time
                                </Typography>
                                {member.time_stats?.spending_time?.formatted &&
                                member.time_stats?.spending_time?.formatted !==
                                  "0s" ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "primary.main",
                                    }}
                                  >
                                    {
                                      member.time_stats?.spending_time
                                        ?.formatted
                                    }
                                  </Typography>
                                ) : (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                  >
                                    <Iconify
                                      icon="eva:info-outline"
                                      sx={{
                                        color: "text.secondary",
                                        width: 14,
                                        height: 14,
                                      }}
                                    />
                                    <Typography
                                      color="text.secondary"
                                      sx={{
                                        fontSize: "11px",
                                      }}
                                    >
                                      No Spent Time
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                            </Paper>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No team stats available
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Stack sx={{ py: 4 }} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              No stats available
            </Typography>
          </Stack>
        )}
      </MuiDrawer>
    </>
  );
}
